// Also future home for ./public/js/designer-flux/utils/PlatformHooks.js

import {getObject, getString} from '@packages/utilities/json';
import {
  type ExpressionPath,
  type DisplayName,
  Component,
  Plugins,
  Expressions,
} from '@packages/systems/wfdl';
import {getName, type Qualified} from '@packages/systems/wfdl-Qualified';

import {
  getInstanceSuffix,
  generateInstanceNativeId,
  getInstanceIndicesFromNativeId,
  getGuidNullable,
  getNativeNodeByNativeIdPath,
  type NativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import {
  getComponentInstRootElement,
  findElement,
  findFurthestAncestor,
} from '@packages/utilities/core-elements';
import {getClosestProjectingElement} from './closestUtils';

export {
  collectFormFields,
  collectFormFieldsFromSitePlugin,
  collectFieldsInsideForm,
} from './collectFormFields';
export type {PageForms, ParsedFormSchema} from './collectFormsForSitePages';
export {
  collectFormsForSite,
  FORM_FIELD_VISIBILITY,
} from './collectFormsForSitePages';
export {
  collectCampaignFormsForSite,
  type CampaignFormResults,
  type PageWithDomNodes,
} from './collectCampaignFormsForSite';
export {collectHubspotFormsForSite} from './collectHubspotFormsForSite';
export {collectMarketoFormsForSite} from './collectMarketoFormsForSite';
export {
  createIntegratedComponent,
  hasFieldType,
} from './createIntegratedComponent';
export * from './ElementClearfixUtils';
export {getTag, getTagFromData, isTagBound} from './getTag';
export * from './SlotDefaultUtils';
export * from './SlotDefaultWriteUtils';
export * from './closestUtils';
export * from './elementTypeSpecific/GridElementUtils';
export * from './elementTypeSpecific/TabsElementUtils';
export * from './elementTypeSpecific/FormElementUtils';
export * from './elementTypeSpecific/SlotElementUtils';
export {
  getNewElementId,
  getNamedElements,
  getElementId,
  collectIdAttributes,
  buildIdAttrMap,
} from './NamedElementUtils';
export type {NamedElementEntry} from './NamedElementUtils';
export {getAttribute} from './getAttribute';
export {isEmptyFormPlaceholder} from './formPlaceholder';
export {getBody} from './elementGetters';
export {getNodeAssetUsage, getStyleBlockAssetUsage} from './getAssetUsage';

// Re-export the entire @packages/utilities/core-elements public API through
// this package's local barrel shim files so `@packages/utilities/platform`
// consumers of ElementTreeUtils, ComponentElementUtils, PageComponent,
// hiddenDataHack, and CurrentComponentUtils symbols keep working
// unchanged. The shims are thin `export * from '@packages/utilities/core-elements'`
// re-exports — they preserve module-load order for platform's cyclic
// consumers (platform-selectors, ContentEditor, etc.).
export * from './ElementTreeUtils';
export * from './ComponentElementUtils';
export * from './PageComponent';
export * from './hiddenDataHack';
export {
  getCurrentComponent,
  getCurrentComponentType,
  getCurrentComponentRender,
} from './CurrentComponentUtils';

export {
  updateComponentRenderInComponentMap,
  updateElementInComponent,
  updateSelectedElement,
  updateOneOfComponents,
  removeUserComponent,
  isComponentDisplayNameTaken,
  generateUniqueComponentName,
} from './ComponentMapUtils';

export * from './hooks';

export {
  isInsideCommerceCartWrapper,
  isInside,
  isInsideCommerceCheckoutFormContainer,
  isInsideCommerceOrderConfirmationContainer,
  isInsideSearchForm,
  isInsidePasswordPageForm,
  isInsideComponentRoot,
  isInsideUserComponentRoot,
  isInsideSearchResultWrapper,
  isInsideSearchResultItem,
  isInsideUserComponentRootOrLocalesWrapper,
  isInsideAnyDynamicList,
  isInsideCommercePaypalCheckoutFormContainer,
  isInsideSlot,
  isInsidePagination,
} from './isInsideUtils';

export * from './expandComponentsUtils';
export * from './TraversalUtils';

export {
  getPathsToAllInstanceRoots,
  isPathToSymbolRoot,
  isSlotDefaultSelection,
  isPathToInstanceDescendant,
  getTargetElementOrInstance,
  getInnermostSymbolInstance,
  getAllComponentInstances,
  getInnermostInstanceRootElement,
  getElementFromNodeInInstanceNativeId,
  getOutermostUserComponentId,
  getOutermostInstanceId,
  getRelativePath,
  isPathContainingInstances,
  isPathContainingNestedInstances,
  isPathTargetWithinFocusedSymbol,
  getOutermostSymbolInstance,
  getTargetElementId,
  getPathsToSymbolRoots,
  isEditingSelectedNodeInInstance,
  visitNativeIdPathSymbolInstances,
  getBoundExpressionAtPath,
  getPathToOutermostSymbolRoot,
  getPathToEnclosingRichText,
  getParentNativeIdPath,
  isPathToTopLevelSymbolInstance,
  nativeIdPathsEqual,
  deriveNativeIdFromPath,
  derivePathFromNativeId,
  getNativeIdPathToSlotDefault,
  generateNativeIdForProjectedElement,
  getNativeIdPathToElement,
  getPathToFocusedComponentRoot,
  isPathInsideNestedSlotDefaultComponent,
  mkMissedFocusSegmentResolver,
} from './NativeIdPathUtils';

export * from './ElementNativeIdUtils';

export {getFieldLabelValidator, getFieldLabels} from './getFieldLabelValidator';

export type {
  TraversalDirection,
  TraversalElementFilter,
} from './NativeIdPathTraversalUtils';
export {
  traversePathInDirection,
  isTraversablePath,
  elementContainsNestedSelection,
  isOrContainsElement,
  NO_ELEMENT_FILTER,
} from './NativeIdPathTraversalUtils';

export {
  transformDesignerNativeIdPath,
  transformDesignerNativeIdPathForOptimization,
  transformDesignerNativeIdPathForOptimizationWithPageSlot,
} from './NativeIdPathTransformer';

export {
  shouldImportComponentFromOrigin,
  getComponentByOrigin,
  getComponentInstOrigin,
  replaceOriginComponentInstancesWithLocalComponents,
  replaceOriginComponentsWithNewPlugin,
  detachAllComponentsFromPlugin,
  detachComponentFromOrigin,
  getOrphanedComponentsByPlugin,
  recoverOrphanedComponents,
  getComponentMapForOrigin,
} from './ComponentOriginUtils';

export {preserveClickGoalBindings} from './preserveClickGoalBindings';

export {
  getUserCustomizedElementLabel,
  getDefaultElementLabel,
  getDisplayLabelForHtmlTagName,
} from './getUserCustomizedElementLabel';

export {
  isOptimizationExperienceElement,
  isOptimizationTreatmentElement,
  isOptimizationBaseElement,
  isOptimizationVariationElement,
  isOptimizationElement,
  isValidElementForOptimization,
  isWholeBlockElementForOptimization,
  getClosestValidElementForOptimization,
  isInsideOptimizationWholeBlockElement,
  OPTIMIZE_CLICK_TRACKING_ATTR_NAME,
  isOptimizeTrackingElement,
  getOptimizationExperienceElementForBaseElement,
  getOptimizationExperienceElementForBaseChild,
  isOptimizationBaseChild,
  componentContainsHtmlEmbed,
  componentContainsCMSElements,
} from './OptimizeTypeUtils';

export {isPassthroughElement} from './isPassthroughElement';

import {
  isRequiredOnlyOnceInSite,
  isSearchForm,
  isCommerceAddToCartWrapper,
  isRequiredOnlyOnceInElement,
  isTypographic,
} from '@packages/utilities/elementTypeUtils';
import {getAncestorProjectingElements} from './TraversalUtils';

import {
  EList,
  EText,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';

export {
  type StyleBlockMapType,
  RelativePositionPlacement,
  type RelativePositionPlacementOptions,
  type PasteElementDetail,
} from './types';

/**
 * Returns the next element that matches the `predicate` starting at the element of `id`.
 */
export function findNextElement(
  expression: Expression,
  id: string,
  predicate: (element: ExpressionElement) => boolean
): ExpressionElement | null {
  const elements = Expressions.allElements(expression);
  const ids = Object.keys(elements);
  let seen = false;

  for (const currentId of ids) {
    const currentElement = elements[currentId];
    if (currentElement && seen && predicate(currentElement)) {
      return currentElement;
    } else if (id === currentId) {
      seen = true;
    }
  }

  return null;
}

/**
 * Returns the native id of an element that was retrieved by walking the
 * provided element tree, taking into account it might be inside a dynlist or
 * any other component that generates n DOM elements from 1 single abstract
 * node. Sometimes, when walking up the element tree, it's required to retrieve
 * the native counterpart of an element. Given that this element can be inside a
 * dynlist, we need to regenerate its native id, based on the original leaf
 * element where the walk started.
 */
export const getNativeIdForProjectedElement = (
  rootElement: Expression,
  ancestorElementId?: string | null,
  leafElementNativeId?: string | null
): NativeId | null => {
  if (!ancestorElementId || !leafElementNativeId) return null;

  const ancestorElement = Expressions.getElementById(
    rootElement,
    ancestorElementId
  );

  if (!ancestorElement) return null;

  const ancestorProjectingElement = getClosestProjectingElement(
    rootElement,
    ancestorElement.id
  );

  // If this isn't a projecting Element or it doesn't have a suffix (i.g:
  // instance-0) just return the Element id.
  if (!ancestorProjectingElement || !getInstanceSuffix(leafElementNativeId)) {
    return ancestorElementId as NativeId;
  }

  // Element must be a projecting node so let's get it's id without the suffix.
  const leafElementId = getGuidNullable(leafElementNativeId);

  const leafElement = leafElementId
    ? Expressions.getElementById(rootElement, leafElementId)
    : null;

  // Fetch all the projecting nodes starting at the leafElement
  const leafProjectingElements = leafElement
    ? getAncestorProjectingElements(leafElement.id, rootElement)
    : [];

  const commonProjectingAncestorIndex = leafProjectingElements.indexOf(
    ancestorProjectingElement
  );

  if (commonProjectingAncestorIndex < 0) {
    /*
      If `ancestorElement` is projected, but either
      1) `leafElement` is not projected, or
      2) `ancestorElement` and `leafElement` do not have a common projecting ancestor,
      then the `ancestorElement` cannot be an ancestor of `leafElement`,
      which means the logic of this function does not apply.
      */
    return null;
  }

  // use `leafNode` instance suffix to generate `ancestorNode` instance suffix
  // by paring it down to the closest common projecting ancestor
  const instances = getInstanceIndicesFromNativeId(leafElementNativeId);
  return generateInstanceNativeId(
    ancestorElementId,
    instances.slice(0, commonProjectingAncestorIndex + 1)
  );
};

export function getDisplayNameForType(
  type: Qualified<Component.ComponentName>,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): DisplayName {
  const componentRoot = componentMap.get(type);
  if (componentRoot) {
    return Component.getDisplayName(componentRoot) || 'Symbol';
  }
  // TODO qualified support to getAtomByType https://app.clickup.com/t/6mqm3n
  return Plugins.getAtomByType(plugins, getName(type)).displayName;
}

function getDisplayNameForElement(
  element: ExpressionElement,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): DisplayName {
  // If a user typed in an explicit Display Name in General Settings, use that as the human-readable
  // name instead of deriving based on element type
  const elementNameRecord = Expressions.getIn(
    Expressions.getElementData(element),
    [{in: 'Record', at: 'displayName'}]
  );

  const elementName = elementNameRecord
    ? Expressions.getText(elementNameRecord)
    : null;

  if (elementName !== null && elementName !== '') {
    return elementName;
  }
  return getDisplayNameForType(element.type, componentMap, plugins);
}

/**
 * Element labels are only leveraged when the element doesn't have a style block.
 *
 * The priority for Element Labels is:
 *
 * 1. Use the displayName directly from the element.
 * 2. Use the displayName on the component instance.
 * 3. Use the displayName on the Atom.
 */
export function getElementLabel(
  element: ExpressionElement,
  currentComponentRender: Expression,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): string {
  return Expressions.getStringFromDisplayName(
    getDisplayNameForElement(element, componentMap, plugins),
    {
      element,
      parentExpression: currentComponentRender,
    }
  );
}

/**
 * Return both the Element and its matching NativeNode given a NativeId
 */
export const getNativeNodeAndElementFromNativeId = ({
  currentComponent,
  focusedSymbolNativeIdPath,
  liveComponentMap,
  nativeNodeId,
  rootElementWithFocusedInstancesExpanded,
  frameId,
}: {
  currentComponent: Component.ComponentType;
  focusedSymbolNativeIdPath: NativeId[];
  liveComponentMap: Component.ComponentMap;
  nativeNodeId: string | null;
  rootElementWithFocusedInstancesExpanded: Expression | null;
  frameId?: string | null;
}): {
  element?: ExpressionElement;
  nativeNode?: null | HTMLElement;
} => {
  const nodeId = getGuidNullable(nativeNodeId);

  const element =
    rootElementWithFocusedInstancesExpanded && nodeId
      ? Expressions.getElementById(
          rootElementWithFocusedInstancesExpanded,
          nodeId
        )
      : null;

  // If we cant find a matching element in our tree, abandon looking for
  // any native node:
  if (!element || !nativeNodeId) {
    return {
      element: undefined,
      nativeNode: undefined,
    };
  }

  // Every element directly in the current page tree (i.e. that isn't nested
  // inside a Component) has a native ID path array that consists solely of
  // the native ID:
  const nativeIdPath = [nativeNodeId];

  // If we're inside a Component, and the provided `nativeNodeId` is a
  // direct descendant of that Component, we expect all native ID paths to
  // start with the ID of the Component *instance* that they're contained in (which
  // is what `currentComponentElement` represents):
  const currentRender = Component.getRender(currentComponent);
  const currentComponentElement =
    nodeId && currentRender
      ? Expressions.getElementById(currentRender, nodeId)
      : null;

  if (currentComponentElement) {
    nativeIdPath.unshift(...focusedSymbolNativeIdPath);
  }

  // In the DOM, a nested Component instance's outermost DOM element will have an
  // additional ID at the end of its native ID path, which corresponds to the ID
  // of the root element *inside the Component*, so we need to append that
  // here if our node is a Component instance:
  let currentElement: ExpressionElement | null = element;
  let componentRootElement;
  const instanceIndexPath = getInstanceIndicesFromNativeId(nativeNodeId);
  while (currentElement != null) {
    componentRootElement =
      getComponentInstRootElement(liveComponentMap)(currentElement);

    if (!componentRootElement) break;
    nativeIdPath.push(
      instanceIndexPath.length > 0
        ? generateInstanceNativeId(componentRootElement.id, instanceIndexPath)
        : componentRootElement.id
    );
    const nestedComponentDefinition = liveComponentMap.get(currentElement.type);
    if (nestedComponentDefinition == null) break;

    // NOTE: If a component has something _other_ than an `Element` expression
    // directly at its root, we will not be able to produce a native ID path for
    // it, because there could be multiple paths it could take, e.g. through a
    // `Case` expression.
    //
    // In order to support this use-case, we must search through the expression
    // tree until we find a path that corresponds to a concrete DOM node.
    //
    // Presumably this will be something we want to do for "stateful" components
    // like e.g. collection lists which have a separate "empty" state that would
    // naturally be represented with a `Case` expression.
    currentElement = Expressions.getElement(
      Component.getRender(nestedComponentDefinition)
    );
  }

  const nativeNode = getNativeNodeByNativeIdPath(nativeIdPath, frameId);
  return {element, nativeNode};
};

export const hasRequiredOnlyOnceInSite = (
  element: ExpressionElement
): null | ExpressionElement => findElement(element, isRequiredOnlyOnceInSite);

export const hasOrphanRequiredOnlyOnceInElement = (
  element: ExpressionElement
): boolean => {
  // Wrapper elements can be copied/cut, deleted etc. but the contents cannot be
  return findElement(element, isSearchForm) ||
    findElement(element, isCommerceAddToCartWrapper)
    ? false
    : findElement(element, isRequiredOnlyOnceInElement) !== null;
};

export const furthestTypographicElement: (
  element: ExpressionElement,
  root: Expression
) => null | {
  data: Expression;
  id: string;
  type: Qualified<Component.ComponentName>;
} = (element: ExpressionElement, root: Expression) =>
  findFurthestAncestor(root, element, isTypographic) || null;

export const ASSET_PATH: ExpressionPath = [{in: 'Record', at: 'img'}];

export const getElementAssetId = (element: ExpressionElement) => {
  const assetExpr = Expressions.getIn(
    Expressions.getElementData(element),
    ASSET_PATH
  );

  const json = assetExpr ? Expressions.getLiteral(assetExpr) : null;
  if (!json) return null;
  const jsonObject = getObject(json.value);
  if (jsonObject && jsonObject.id) {
    return getString(jsonObject.id);
  }
  return null;
};

export const updateXscpStyleBlockIds =
  (styleBlockIdMap: {[key: string]: string}) => (tree: Expression) =>
    Expressions.everywhere(updateXscpStyleBlockId(styleBlockIdMap))(tree);

const DATA_STYLEBLOCKIDS: ExpressionPath = [
  {in: 'Record', at: 'styleBlockIds'},
];
const DATA_STYLEBLOCKIDS_IN_ELEMENT: ExpressionPath = [
  {in: 'Element'},
  {in: 'Record', at: 'styleBlockIds'},
];

const DATA_STYLEBLOCKIDS_OVERRIDES: ExpressionPath = [
  {in: 'Record', at: 'sym'},
  {in: 'Record', at: 'overrides'},
  {in: 'Record', at: 'styleBlockIds'},
];
const DATA_STYLEBLOCKIDS_OVERRIDES_IN_ELEMENT: ExpressionPath = [
  {in: 'Element'},
  {in: 'Record', at: 'sym'},
  {in: 'Record', at: 'overrides'},
  {in: 'Record', at: 'styleBlockIds'},
];

const updateStyleBlockId = (
  expressionToUpdate: Expression,
  styleBlockIdMap: {
    [key: string]: string;
  }
) => {
  const list = Expressions.getList(expressionToUpdate);
  return list
    ? EList(
        list.map((expr) => {
          const id = Expressions.getText(expr);
          if (!id) {
            return expr;
          }
          const styleBlockId = styleBlockIdMap[id];
          if (!styleBlockId) {
            return expr;
          }
          return EText(styleBlockId);
        })
      )
    : expressionToUpdate;
};

const updateXscpStyleBlockId =
  (styleBlockIdMap: {[key: string]: string}) =>
  (expression: Expression): Expression => {
    const element = Expressions.getElement(expression);
    if (!element) {
      return expression;
    }
    const elementData = Expressions.getElementData(element);
    if (Expressions.getIn(elementData, DATA_STYLEBLOCKIDS)) {
      return Expressions.updateIn(
        expression,
        DATA_STYLEBLOCKIDS_IN_ELEMENT,
        (expressionToUpdate) =>
          updateStyleBlockId(expressionToUpdate, styleBlockIdMap)
      );
    }

    if (Expressions.getIn(elementData, DATA_STYLEBLOCKIDS_OVERRIDES)) {
      return Expressions.updateIn(
        expression,
        DATA_STYLEBLOCKIDS_OVERRIDES_IN_ELEMENT,
        (expressionToUpdate) =>
          updateStyleBlockId(expressionToUpdate, styleBlockIdMap)
      );
    }
    return expression;
  };

export * from './getStyleBlocks';
export {isUnselectableElement} from './isUnselectableElement';
export {isRichTextDescendant} from './isRichTextDescendant';
export {isComponentOnlyPageSlotDescendant} from './isComponentOnlyPageSlotDescendant';
export {isComponentInsidePageSlot} from './isComponentInsidePageSlot';
